import { Action, Dispatch } from 'redux';

import { ATTACHMENT_URL, HttpStatusCode, MAX_UPLOAD_LIMIT } from 'appConstants';
import { AppState } from 'reducers';
import {
  ApiRequest,
  ApiRequestError,
  AttachmentErrors,
  AttachmentUtils,
} from 'services';
import { Attachment, FileInfo } from 'types';

/* Types */
export interface AttachmentsAction extends Action {
  readonly attachment?: Attachment;
}

export interface AttachmentItems {
  [key: string]: Attachment;
}

export interface Attachments {
  readonly items: AttachmentItems;
}

interface Payload {
  filename: string;
  ticketId?: string;
}

/* Initial State */
const initialState: Attachments = {
  items: {},
};

/* Actions */
enum Actions {
  REMOVE_ATTACHMENT = 'REMOVE_ATTACHMENT',
  RESET_ATTACHMENT_STATE = 'RESET_ATTACHMENT_STATE',
  UPSERT_ATTACHMENT = 'UPSERT_ATTACHMENT',
}

const removeAttachment = (attachment: Attachment): AttachmentsAction => ({
  type: Actions.REMOVE_ATTACHMENT,
  attachment,
});

const resetAttachmentState = (): AttachmentsAction => ({
  type: Actions.RESET_ATTACHMENT_STATE,
});

const upsertAttachment = (attachment: Attachment): AttachmentsAction => ({
  type: Actions.UPSERT_ATTACHMENT,
  attachment,
});

/* Reducer */
const attachmentsReducer = (
  state = initialState,
  action: AttachmentsAction,
) => {
  let attachment;

  switch (action.type) {
    case Actions.UPSERT_ATTACHMENT:
      attachment = action.attachment || ({} as Attachment);
      return {
        ...state,
        items: { ...state.items, [attachment.id]: action.attachment },
      };
    case Actions.REMOVE_ATTACHMENT:
      attachment = action.attachment || ({} as Attachment);
      // Create new object before mutating
      const newState = { ...state.items };
      delete newState[attachment.id];
      return {
        ...state,
        items: newState,
      };
    case Actions.RESET_ATTACHMENT_STATE:
      return {
        ...state,
        items: {},
      };
    default:
      return state;
  }
};

/* Async Helpers */
const requestNewAttachment = async (
  fileInfo: FileInfo,
  ticketId: string = '',
) => {
  let payload: Payload = {
    filename: fileInfo.filename,
  };

  if (ticketId) {
    payload.ticketId = ticketId;
  }

  const onError = (error: ApiRequestError) => {
    const errorType =
      error.status === HttpStatusCode.BAD_REQUEST
        ? AttachmentErrors.INVALID_EXTENSION
        : AttachmentErrors.ATTACHMENT_DATA_FAILURE;
    fileInfo.errors.add(errorType);
  };

  const attachmentData = await ApiRequest.post(ATTACHMENT_URL, payload, {
    transform: AttachmentUtils.pickAttachmentData,
    onError,
  });

  return AttachmentUtils.composeAttachment(fileInfo, attachmentData);
};

/* Thunks */
const sendUpload = (attachment: Attachment) => async (
  dispatch: Dispatch<AttachmentsAction>,
) => {
  if (!attachment.data || attachment.errors.size) {
    return;
  }

  const { signature, max_file_size, max_file_count, expires } = attachment.data;

  // Format body and upload to cloud
  const body = new FormData();
  body.append('signature', signature);
  body.append('max_file_size', max_file_size.toString());
  body.append('max_file_count', max_file_count.toString());
  body.append('expires', expires.toString());
  body.append('filename', attachment.content.name);
  body.append('upload', attachment.content);

  let url = attachment.data.url;
  url = url.slice(url.indexOf('v1/MossoCloudFS_'));

  const onUpdate = (result: Attachment) => dispatch(upsertAttachment(result));

  return ApiRequest.upload(attachment, body, url, onUpdate);
};

const uploadFiles = (files: File[], ticketId?: string) => async (
  dispatch: Dispatch<AttachmentsAction>,
  getState: Function,
) => {
  // Check total valid files uploaded so far
  const state: AppState = getState();
  const attachmentsCount = Object.keys(state.attachments.items).length;

  // Don't upload more files if it would go over the limit
  if (files.length + attachmentsCount > MAX_UPLOAD_LIMIT) {
    /*
     * TODO: dispatch "Too many files" error notification
     * https://jira.rax.io/browse/SDP-1682
     */

    return;
  }

  await Promise.all(
    files.map(async (file) => {
      // Process each file
      const fileInfo = AttachmentUtils.processFile(file);

      if (!fileInfo.isValid) {
        // Dispatch invalid attachments if invalid
        return dispatch(upsertAttachment(fileInfo as Attachment));
      }

      // Request new attachment to begin upload
      const attachment = await requestNewAttachment(fileInfo, ticketId);

      // Attachment request was successful so begin upload
      if (attachment.data) {
        dispatch(sendUpload(attachment));
      }

      return dispatch(upsertAttachment(attachment));
    }),
  );
};

const retryUpload = (attachmentId: string = 'all', ticketId?: string) => async (
  dispatch: Dispatch<AttachmentsAction>,
  getState: Function,
) => {
  // Check total valid files uploaded so far
  const state: AppState = getState();
  const attachments = state.attachments.items;

  if (attachmentId !== 'all' && !(attachmentId in attachments)) {
    return;
  }

  const toRetry: Attachment[] =
    attachmentId === 'all'
      ? AttachmentUtils.withUploadErrors(attachments)
      : [attachments[attachmentId]];

  await Promise.all(
    toRetry.map(async (attachment) => {
      // Return if no error as an extra precaution
      if (!attachment.errors.size) {
        return;
      }

      if (attachment.errors.has(AttachmentErrors.ATTACHMENT_DATA_FAILURE)) {
        // Request new attachment to begin upload
        attachment = await requestNewAttachment(
          attachment as FileInfo,
          ticketId,
        );
      }

      if (attachment.data) {
        dispatch(sendUpload(attachment));
      }

      return dispatch(upsertAttachment(attachment));
    }),
  );
};

export {
  attachmentsReducer,
  removeAttachment,
  requestNewAttachment,
  resetAttachmentState,
  retryUpload,
  sendUpload,
  uploadFiles,
  upsertAttachment,
};
